/**
 * WorkerBridge — main-thread interface to the inference Web Worker.
 *
 * Manages the worker lifecycle, sends event batches via postMessage,
 * and handles the flush/destroy protocol.  The worker URL is injected
 * at build time as an inline blob.
 */

import type { WorkerInbound, WorkerOutbound } from "./protocol";

export interface WorkerBridgeOptions {
  apiUrl: string;
  apiKey: string;
  syncIntervalMs: number;
  debug: boolean;
  sessionId: string | null;
  /** Called when the worker reports a session ID from the backend. */
  onSessionId?: (id: string) => void;
  /** Called after each successful sync round-trip. */
  onSyncSuccess?: () => void;
}

export class WorkerBridge {
  private worker: Worker | null = null;
  private ready = false;
  private queue: WorkerInbound[] = [];
  private opts: WorkerBridgeOptions;

  constructor(opts: WorkerBridgeOptions) {
    this.opts = opts;
  }

  /**
   * Spawn the worker.  The `workerUrl` is typically a blob URL created
   * by the esbuild bundle (inlined at build time).
   */
  start(workerUrl: string | URL): void {
    try {
      this.worker = new Worker(workerUrl, { type: "classic" });
    } catch {
      if (this.opts.debug) {
        console.warn(
          "[signal] Worker creation failed, falling back to main thread",
        );
      }
      return;
    }

    this.worker.onmessage = (e: MessageEvent<WorkerOutbound>) => {
      const msg = e.data;
      switch (msg.type) {
        case "ready":
          this.ready = true;
          for (const queued of this.queue) {
            this.send(queued);
          }
          this.queue = [];
          break;

        case "session-id":
          this.opts.onSessionId?.(msg.sessionId);
          break;

        case "sync-success":
          this.opts.onSyncSuccess?.();
          break;
      }
    };

    this.worker.onerror = (e) => {
      if (this.opts.debug) {
        console.warn("[signal] Worker error:", e.message);
      }
    };

    this.send({
      type: "init",
      apiUrl: this.opts.apiUrl,
      apiKey: this.opts.apiKey,
      syncIntervalMs: this.opts.syncIntervalMs,
      debug: this.opts.debug,
      sessionId: this.opts.sessionId,
    });
  }

  /** Whether the worker was successfully created and is running. */
  get isActive(): boolean {
    return this.worker !== null;
  }

  /** Post a batch of axos events (with current page context) to the worker. */
  postEvents(
    events: Record<string, unknown>[],
    url: string,
    title: string,
  ): void {
    this.send({ type: "events", events, url, title });
  }

  /** Tell the worker to flush any pending request immediately. */
  flush(): void {
    this.send({ type: "flush" });
  }

  /** Set the session ID on the worker (e.g. from an external source). */
  setSessionId(id: string): void {
    this.send({ type: "set-session-id", sessionId: id });
  }

  /** Terminate the worker. Flushes pending data first. */
  destroy(): void {
    if (!this.worker) return;

    this.send({ type: "destroy" });

    // Give the worker a moment to flush, then hard-terminate
    setTimeout(() => {
      this.worker?.terminate();
      this.worker = null;
      this.ready = false;
    }, 500);
  }

  private send(msg: WorkerInbound): void {
    if (!this.worker) return;

    if (!this.ready && msg.type !== "init") {
      this.queue.push(msg);
      return;
    }

    this.worker.postMessage(msg);
  }
}
